import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  DocsEditor,
  type DocumentContent,
  toDocumentNode,
  toEditorContent,
} from '@burfy/docs-editor';
import { AssetStoreProvider, PageSetupDialog } from '@burfy/docs-editor/ui';
import {
  ExportDialog,
  type ExportFormat,
  exportDocument,
  printDocument,
} from '@burfy/docs-editor/print';
import {
  type BurfyDocument,
  DocumentConflictError,
  type DocumentStore,
  type PageSetup,
  createPageGeometry,
} from '@burfy/docs-core';
import { isLanguageCode, type LanguageCode } from '@burfy/input-tools';
import { IndexedDbLearningStore, assetStore } from '../storage/index.ts';
import { LocalFontProvider } from '../fonts/index.ts';
import { useAutosave } from '../hooks/useAutosave.ts';
import { SaveIndicator } from './SaveIndicator.tsx';
import { NoticeBar, type Notice } from './NoticeBar.tsx';
import { createEngines, resolveEnginePreference } from '../voice/index.ts';
import { createDocumentMenus } from './document-menus.ts';

/**
 * What the user edits.
 *
 * Deliberately excludes `rev`: the revision belongs to the *stored* document,
 * not to the draft. Folding it in would mean every successful save produced a
 * new value, which would trigger another save, for ever.
 */
interface Draft {
  title: string;
  content: DocumentContent;
  pageSetup: PageSetup;
  /** Input language for transliteration — part of the document, not the view. */
  language: LanguageCode;
}

/**
 * One store for the whole app: learned choices are the user's, not any
 * particular document's, and a per-document instance would open a redundant
 * database connection for each one.
 */
const learningStore = new IndexedDbLearningStore();

/** Shared too: uploaded fonts belong to the browser, not to one document. */
const fontProvider = new LocalFontProvider();

const ZOOM_LEVELS = [0.5, 0.75, 0.9, 1, 1.25, 1.5, 2];

type HeaderIconName = 'back' | 'download' | 'page' | 'print';

function HeaderIcon({ name }: { name: HeaderIconName }) {
  const paths: Record<HeaderIconName, ReactNode> = {
    back: (
      <>
        <path d="m15 18-6-6 6-6" />
        <path d="M9 12h10" />
      </>
    ),
    download: (
      <>
        <path d="M12 3v12M7 10l5 5 5-5" />
        <path d="M5 21h14" />
      </>
    ),
    page: (
      <>
        <path d="M6 3h8l4 4v14H6z" />
        <path d="M14 3v5h4M9 13h6M9 17h6" />
      </>
    ),
    print: (
      <>
        <path d="M7 8V3h10v5M7 17v4h10v-4" />
        <rect x="4" y="8" width="16" height="9" rx="2" />
        <path d="M16 12h1" />
      </>
    ),
  };

  return (
    <svg
      className="burfy-app-header-icon"
      viewBox="0 0 24 24"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {paths[name]}
    </svg>
  );
}

export interface DocumentEditorProps {
  /** The loaded document. Mount this component with `key={document.id}`. */
  document: BurfyDocument;
  store: DocumentStore;
}

export function DocumentEditor({ document, store }: DocumentEditorProps) {
  const [draft, setDraft] = useState<Draft>(() => ({
    title: document.meta.title,
    content: toEditorContent(document.content),
    pageSetup: document.meta.pageSetup,
    // Documents written before the IME existed carry no language; English is
    // the safe default because it sends nothing anywhere.
    language: isLanguageCode(document.meta.language) ? document.meta.language : 'en',
  }));

  // Zoom is a viewing preference, not a property of the document: it is not
  // saved, and it never reaches the layout engine. Ruler and outline are the
  // same kind of thing.
  const [zoom, setZoom] = useState(1);
  /** What the editor actually drew, which differs once the page is fitted. */
  const [renderZoom, setRenderZoom] = useState(1);
  const [showRuler, setShowRuler] = useState(true);
  const [showOutline, setShowOutline] = useState(true);
  const [pageCount, setPageCount] = useState(1);
  const [setupOpen, setSetupOpen] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [exportOpen, setExportOpen] = useState(false);

  // Built once: the engines hold no per-document state, and handing the editor
  // a new array each render would restart the microphone mid-sentence.
  const voice = useMemo(
    () => ({ preference: resolveEnginePreference(), engines: createEngines() }),
    [],
  );

  /** True when the page had to be shrunk to fit the viewport. */
  const fitted = Math.abs(renderZoom - zoom) > 0.01;
  const fittedPercent = Math.round(renderZoom * 100);

  const navigate = useNavigate();

  /** The last version known to be in storage, including its revision. */
  const baseRef = useRef<BurfyDocument>(document);

  const save = useCallback(
    async (value: Draft) => {
      const base = baseRef.current;
      baseRef.current = await store.save({
        ...base,
        meta: {
          ...base.meta,
          title: value.title,
          pageSetup: value.pageSetup,
          language: value.language,
        },
        content: toDocumentNode(value.content),
      });
    },
    [store],
  );

  const autosave = useAutosave<Draft>({ value: draft, save });
  const conflicted = autosave.error instanceof DocumentConflictError;

  const handlePageCountChange = useCallback((count: number) => setPageCount(count), []);

  /** The document as it looks on screen, not as it was last written. */
  const currentDocument = useCallback(
    (): BurfyDocument => ({
      ...baseRef.current,
      meta: {
        ...baseRef.current.meta,
        title: draft.title,
        pageSetup: draft.pageSetup,
      },
      content: toDocumentNode(draft.content),
    }),
    [draft],
  );

  const print = useCallback(() => {
    printDocument(createPageGeometry(draft.pageSetup));
  }, [draft.pageSetup]);

  const runExport = useCallback(
    (format: ExportFormat) => {
      setExportOpen(false);
      setNotice(null);

      /*
       * PDF goes through the browser's print dialog, which is modal and
       * blocking — it must not open until React has taken the export dialog
       * off screen, or the print preview contains a picture of the dialog the
       * user just dismissed. Two frames: one for React to commit, one for the
       * browser to paint it.
       */
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // Only if it is slow enough to be worth saying: a DOCX of a long
          // document takes a moment, a plain-text one is instant, and a
          // message that flashes past is worse than none.
          const slow = window.setTimeout(
            () => setNotice({ tone: 'info', text: 'Preparing your download…' }),
            400,
          );

          void exportDocument({ document: currentDocument(), format, assetStore })
            .then(() => setNotice(null))
            .catch((error: unknown) => {
              console.error('[burfy-docs] export failed', error);
              setNotice({ tone: 'error', text: 'That export could not be created.' });
            })
            .finally(() => window.clearTimeout(slow));
        });
      });
    },
    [currentDocument],
  );

  const menuExtras = useMemo(
    () =>
      createDocumentMenus(
        { zoom, zoomLevels: ZOOM_LEVELS, showRuler, showOutline },
        {
          onNewDocument: () => {
            void store.create().then((created) => navigate(`/d/${created.id}`));
          },
          onAllDocuments: () => {
            void navigate('/');
          },
          onExport: () => setExportOpen(true),
          onPageSetup: () => setSetupOpen(true),
          onZoomChange: setZoom,
          onToggleRuler: () => setShowRuler((current) => !current),
          onToggleOutline: () => setShowOutline((current) => !current),
        },
      ),
    [zoom, showRuler, showOutline, store, navigate],
  );

  return (
    <div className="flex h-full flex-col bg-white">
      <header className="burfy-docs-no-print burfy-app-editor-header">
        <Link to="/" className="burfy-app-back-button" aria-label="Back to documents">
          <HeaderIcon name="back" />
        </Link>

        <span className="burfy-app-document-mark" aria-hidden="true">
          <HeaderIcon name="page" />
        </span>

        <div className="burfy-app-title-group">
          <input
            className="burfy-app-title-input"
            aria-label="Document title"
            value={draft.title}
            onChange={(event) =>
              setDraft((current) => ({ ...current, title: event.target.value }))
            }
          />
          <div className="burfy-app-document-meta" aria-hidden="true">
            Burfy Docs <span>•</span> {pageCount} {pageCount === 1 ? 'page' : 'pages'}
          </div>
        </div>

        <select
          className="burfy-app-zoom-select"
          aria-label="Zoom"
          // Says what is really on screen when the page has been shrunk to
          // fit, rather than reporting the multiplier and leaving the reader
          // to wonder why 100% looks like half size.
          title={
            fitted
              ? `Zoom ${Math.round(zoom * 100)}%, shown at ${fittedPercent}% to fit the screen`
              : 'Zoom'
          }
          value={String(zoom)}
          onChange={(event) => setZoom(Number(event.target.value))}
        >
          {ZOOM_LEVELS.map((level) => (
            <option key={level} value={level}>
              {Math.round(level * 100)}%
            </option>
          ))}
        </select>

        <button
          type="button"
          className="burfy-app-header-button"
          onClick={() => setSetupOpen(true)}
        >
          <HeaderIcon name="page" />
          Page setup
        </button>

        <button
          type="button"
          className="burfy-app-header-button"
          onClick={() => setExportOpen(true)}
        >
          <HeaderIcon name="download" />
          Download
        </button>

        <button
          type="button"
          className="burfy-app-header-button burfy-app-header-button-primary"
          onClick={print}
        >
          <HeaderIcon name="print" />
          Print
        </button>

        {fitted && (
          <span
            className="burfy-app-fit-note"
            title="The page is scaled so the whole width fits on screen. Page breaks are unchanged."
          >
            fit {fittedPercent}%
          </span>
        )}

        <SaveIndicator status={autosave.status} conflicted={conflicted} />
      </header>

      <main className="min-h-0 flex-1">
        <DocsEditor
          content={draft.content}
          pageSetup={draft.pageSetup}
          defaultStyle={document.meta.defaultStyle}
          zoom={zoom}
          showRuler={showRuler}
          showOutline={showOutline}
          menuExtras={menuExtras}
          footerText={draft.title}
          language={draft.language}
          onPageSetupChange={(pageSetup) =>
            setDraft((current) => ({ ...current, pageSetup }))
          }
          learning={learningStore}
          assetStore={assetStore}
          fontProvider={fontProvider}
          onImageError={(text) => setNotice({ tone: 'error', text })}
          voice={voice}
          onVoiceError={(text) => setNotice({ tone: 'error', text })}
          onLanguageChange={(language) =>
            setDraft((current) => ({ ...current, language }))
          }
          onPageCountChange={handlePageCountChange}
          onRenderZoomChange={setRenderZoom}
          onChange={(content) => setDraft((current) => ({ ...current, content }))}
        />
      </main>

      {notice && <NoticeBar notice={notice} onDismiss={() => setNotice(null)} />}

      {exportOpen && (
        <ExportDialog onCancel={() => setExportOpen(false)} onExport={runExport} />
      )}

      {setupOpen && (
        // The dialog lives outside `<DocsEditor>`, so it is outside the
        // provider that component sets up — and it needs the store to import
        // a page background image.
        <AssetStoreProvider store={assetStore}>
          <PageSetupDialog
            value={draft.pageSetup}
            onCancel={() => setSetupOpen(false)}
            onApply={(pageSetup) => {
              setDraft((current) => ({ ...current, pageSetup }));
              setSetupOpen(false);
            }}
          />
        </AssetStoreProvider>
      )}
    </div>
  );
}
